import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Alert,
	Link,
	Tabs,
	Tab,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon, DeleteOutline as DeleteIcon, Email } from "@mui/icons-material";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import customHttp from "../../services/customHTTP";
import "./Cart.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useNavigate } from "react-router-dom";
import { ASSETS_BASE_URL } from "../../config";
import { useAuth } from "../../context/authContext";
import Requests from "../Requests/Requests";
import RequestList from "../../components/RequestList/RequestList";
import NotifyPurchaser from "../../components/NotifyPurchaser/NotifyPurchaser";
// import SavedForLater from "../SavedForLater/SavedForLater";

function Cart({ openRequests }) {
	const { cartItems, removeFromCart, clearCart, updateCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol } = useSite();
	const { user } = useAuth();
	const [tab, setTab] = useState(openRequests ? 1 : 0);
	const [pageTitle, setPageTitle] = useState(openRequests ? "Cart: Requests" : "Cart");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [cartProducts, setCartProducts] = useState([]);
	const [groupedCartProducts, setGroupedCartProducts] = useState([]);
	const [tempQuantities, setTempQuantities] = useState({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [appOptions, setAppOptions] = useState({
		currency: "USD",
		minOrderAmount: 0,
		creditCardFee: 2.5,
		taxRate: 5,
		shippingCost: 10,
		leadTime: 3,
	});
	const navigate = useNavigate();

	const handleNavigateToProduct = (url) => {
		navigate(url);
	};

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
		if (newValue === 1) {
			setPageTitle(`Cart: Requests`);
		} else {
			setPageTitle(`Cart`);
		}
	};

	// Check if product image exists
	const checkImageExists = async (productNumber) => {
		const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${productNumber}.png`;
		try {
			const response = await fetch(productImageUrl, { method: "HEAD" });
			return response.ok ? productImageUrl : `${ASSETS_BASE_URL}/stock.png`;
		} catch (error) {
			console.warn("Product image not found, using default image.");
			return `${ASSETS_BASE_URL}/stock.png`;
		}
	};

	useEffect(() => {
		console.log("User From Cart: ", user);
		const fethCTP = async (filteredCartItems) => {
			const updatedItems = await Promise.all(
				filteredCartItems.map(async (item) => {
					let lead_days = item.Product.product_sell_end_date;
					lead_days = parseInt(lead_days, 10);

					let ctp, ctp_display;
					if (isNaN(lead_days)) {
						ctp = "To Be Decided";
						ctp_display = "To Be Decided";
					} else {
						const today = new Date();
						const futureDate = new Date();
						futureDate.setDate(today.getDate() + lead_days);

						// ISO string for backend or sorting
						ctp = futureDate.toISOString().split("T")[0];

						if (lead_days < 18) {
							// Force display for short lead times
							ctp_display = "2 - 3 weeks";
						} else {
							const minWeeks = Math.floor(lead_days / 7);
							const maxWeeks = Math.ceil(lead_days / 7);

							const adjustedMin = Math.max(minWeeks - 1, 1); // Ensure at least 1

							if (adjustedMin === maxWeeks) {
								ctp_display = `${adjustedMin} week${adjustedMin > 1 ? "s" : ""}`;
							} else {
								ctp_display = `${adjustedMin} - ${maxWeeks} weeks`;
							}
						}
					}

					return {
						...item,
						ctp,
						ctp_display,
					};
				})
			);

			return updatedItems;
		};

		const fetchProducts = async () => {
			setLoading(true);
			setError(null);

			const filteredCartItems = cartItems.filter((item) => item.Site === selectedSite);
			console.log(cartItems);
			if (filteredCartItems.length === 0) {
				// No items, clear state but ensure it only updates after initialization
				setCartProducts([]);
				setLoading(false);
				setIsInitialized(true); // Mark as initialized even if empty
				return;
			}

			try {
				const reqBody = {
					products: [
						...new Set(
							filteredCartItems
								.filter((item) => item.ProductID && item.ProductID !== "" && item.Site === selectedSite)
								.map((item) => item.ProductID)
						),
					],
				};

				const response = await customHttp.post("/products/byids", reqBody);
				const productData = response.data?.data || [];
				console.log(productData);

				const updatedCartItems = await Promise.all(
					filteredCartItems.map(async (item, index) => {
						const matchedProduct = productData.find((product) => product.product_id === item.ProductID);
						if (matchedProduct) {
							const imageSrc = await checkImageExists(matchedProduct.product_number);
							return {
								...item,
								Product: matchedProduct,
								imageSrc,
							};
						}
						return item;
					})
				);
				const updatedCartItemsWithCTP = await fethCTP(updatedCartItems);
				console.log("CTP: ", updatedCartItemsWithCTP);
				setCartProducts(updatedCartItemsWithCTP);
			} catch (err) {
				setError("Failed to fetch data: " + err.message);
			} finally {
				setLoading(false);
				setIsInitialized(true); // Mark as initialized after fetch
			}
		};

		const fetchOptions = async () => {
			try {
				const response = await customHttp.get("options");
				const responseData = response.data.options;
				setAppOptions(responseData);
			} catch (error) {
				console.log(error);
			}
		};

		// Fetch products only if there are cart items
		if (cartItems.length > 0) {
			fetchProducts();
			fetchOptions();
		} else {
			setCartProducts([]); // Clear cart if no items
			setLoading(false);
			setIsInitialized(true); // Mark as initialized even if no items
		}
	}, [selectedSite, cartItems]);

	useEffect(() => {
		console.log("Cart Products: ", cartProducts);
		const getgroupedCartProducts = cartProducts.reduce((groups, item) => {
			const { ctp_display } = item;
			if (!groups[ctp_display]) {
				groups[ctp_display] = [];
			}
			groups[ctp_display].push(item);
			return groups;
		}, {});
		console.log(getgroupedCartProducts);
		setGroupedCartProducts(getgroupedCartProducts);
	}, [cartProducts]);

	const handleUpdateQuantity = (productId, change) => {
		setCartProducts((prevProducts) => {
			const updatedProducts = prevProducts.map((item) => {
				if (item.Product.product_id === productId) {
					const newQuantity = Math.max(1, Math.min(item.Quantity + change, 9999));
					return {
						...item,
						Quantity: newQuantity,
					};
				}
				return item;
			});

			// Call updateCart after updating the state
			updateCart(updatedProducts);
			return updatedProducts;
		});
	};

	const handleQuantityChange = (productId, newValue) => {
		const quantity = Math.max(1, parseInt(newValue) || 1);

		// Access cartProducts directly to fetch current stock
		setCartProducts((prevProducts) => {
			const updatedProducts = prevProducts.map((item) => {
				if (item.Product.product_id === productId) {
					const newQuantity = Math.min(quantity, 9999);
					return {
						...item,
						Quantity: newQuantity,
					};
				}
				return item;
			});

			// Call updateCart after updating the state
			updateCart(updatedProducts);
			return updatedProducts;
		});

		// Update temporary state with the current input
		setTempQuantities((prevQuantities) => {
			// Find the stock for the product to ensure it doesn't exceed
			const product = cartProducts.find((item) => item.Product.product_id === productId);
			const maxQuantity = 9999;

			return {
				...prevQuantities,
				[productId]: Math.min(quantity, maxQuantity),
			};
		});
	};

	// Commit changes to the cart when the input loses focus
	const handleBlur = (productId) => {
		const quantity = tempQuantities[productId] || 1;

		setCartProducts((prevProducts) => {
			const updatedProducts = prevProducts.map((item) =>
				item.Product.product_id === productId ? { ...item, Quantity: quantity } : item
			);

			// Update the cart after changes
			updateCart(updatedProducts);
			return updatedProducts;
		});
	};

	// Keep tempQuantities in sync with cartProducts
	useEffect(() => {
		const initialQuantities = {};
		cartProducts.forEach((item) => {
			initialQuantities[item.Product.product_id] = item.Quantity;
		});
		setTempQuantities(initialQuantities);
	}, [cartProducts]);

	const handleRemoveItem = (productId) => {
		removeFromCart(productId);
		setCartProducts((prev) => prev.filter((item) => item.Product.product_id !== productId));
	};

	const parsePrice = (price) => {
		const numPrice = typeof price === "string" ? parseFloat(price) : price;
		return isNaN(numPrice) ? 0 : numPrice;
	};

	const calculateSubtotal = () => {
		return cartProducts.reduce((total, item) => {
			const key = `price_${selectedSiteCurrency}`;
			let currencyPrice = item.Product[key];
			if (currencyPrice == "0") {
				currencyPrice = item.Product.product_sell_price;
			}
			const finalPrice = item.Product.purchasable ? currencyPrice : 0;
			const price = parsePrice(finalPrice);
			return total + price * item.Quantity;
		}, 0);
	};

	const calculateTax = () => calculateSubtotal() * 0; // 10% tax
	const calculateTotal = () => calculateSubtotal() + calculateTax();
	if (loading) {
		return (
			<Box className="cart-loader">
				<CircularProgress />
			</Box>
		);
	}

	if (!isInitialized) {
		return (
			<Box className="cart-loader">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	return (
		<Container maxWidth="xl" className="cart-container">
			<PageTitle title={pageTitle} />
			<Tabs value={tab} onChange={handleTabChange} aria-label="profile page tabs" className="profile-tabs">
				<Tab label="Cart" />
				<Tab label="Request List" />
			</Tabs>

			{tab === 0 && (
				<>
					{cartProducts.length === 0 && !loading ? (
						<Alert severity="info" className="empty-cart-alert">
							Your cart is empty. Continue shopping to add items to your cart.
						</Alert>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								gap: "24px",
							}}>
							{/* Cart Items Section */}
							<Box sx={{ flex: 1 }}>
								<Card className="cart-items-card">
									<CardContent>
										<TableContainer>
											<Table>
												<TableHead>
													<TableRow>
														<TableCell>Part(s)</TableCell>
														<TableCell align="center">Quantity</TableCell>
														<TableCell align="right">Price</TableCell>
														<TableCell align="right">Total</TableCell>
														<TableCell></TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{Object.keys(groupedCartProducts).map((ctpDate) => {
														const group = groupedCartProducts[ctpDate];
														const displayText = group?.[0]?.ctp_display || ctpDate;
														return (
															<React.Fragment key={ctpDate}>
																<Typography
																	variant="body1"
																	sx={{ margin: "20px 10px 10px 10px !important" }}>
																	<span style={{ color: "green" }}>
																		Estimated Shipping Date:
																	</span>{" "}
																	{displayText}
																</Typography>
																{groupedCartProducts[ctpDate].map((item) => (
																	<TableRow
																		key={item.Product.product_id}
																		sx={{
																			"&:last-child td, &:last-child th": {
																				border: 0,
																			},
																		}}>
																		<TableCell>
																			<Box className="product-cell">
																				<img
																					src={item.imageSrc}
																					alt={item.Product.product_name}
																					className="product-image"
																				/>
																				<Box className="product-info">
																					<Link
																						underline="hover"
																						sx={{
																							cursor: "pointer",
																						}}
																						onClick={() => {
																							const url = `/machines/${item.Product.machine_id}/${item.Product.product_id}`;
																							handleNavigateToProduct(
																								url
																							);
																						}}>
																						{item.Product.product_name}
																					</Link>
																					<Typography
																						variant="body2"
																						color="textSecondary">
																						Part #:{" "}
																						{item.Product.product_number}
																					</Typography>
																				</Box>
																			</Box>
																		</TableCell>
																		<TableCell align="center">
																			<Box className="quantity-controls">
																				<IconButton
																					size="small"
																					onClick={() =>
																						handleUpdateQuantity(
																							item.Product.product_id,
																							-1
																						)
																					}>
																					<RemoveIcon />
																				</IconButton>
																				<TextField
																					type="number"
																					size="small"
																					value={
																						tempQuantities[
																							item.Product.product_id
																						] || item.Quantity
																					}
																					onChange={(e) =>
																						handleQuantityChange(
																							item.Product.product_id,
																							e.target.value
																						)
																					}
																					onBlur={() =>
																						handleBlur(
																							item.Product.product_id
																						)
																					}
																					className="quantity-input"
																					InputProps={{
																						inputProps: {
																							min: 1,
																							max: 99,
																						},
																					}}
																					sx={{
																						"& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
																							{
																								"-webkit-appearance":
																									"none",
																								margin: 0,
																							},
																						"& input[type=number]": {
																							"-moz-appearance":
																								"textfield",
																						},
																					}}
																				/>
																				<IconButton
																					size="small"
																					onClick={() =>
																						handleUpdateQuantity(
																							item.Product.product_id,
																							1
																						)
																					}>
																					<AddIcon />
																				</IconButton>
																			</Box>
																		</TableCell>
																		<TableCell align="right">
																			{currencySymbol}
																			{parsePrice(
																				item.Product[
																					`price_${selectedSiteCurrency}`
																				] === "0"
																					? item.Product.product_sell_price
																					: item.Product[
																							`price_${selectedSiteCurrency}`
																					  ]
																			).toFixed(2)}
																		</TableCell>
																		<TableCell align="right">
																			{currencySymbol}
																			{(
																				parsePrice(
																					item.Product[
																						`price_${selectedSiteCurrency}`
																					] === "0"
																						? item.Product
																								.product_sell_price
																						: item.Product[
																								`price_${selectedSiteCurrency}`
																						  ]
																				) * item.Quantity
																			).toFixed(2)}
																		</TableCell>
																		<TableCell>
																			<IconButton
																				color="primary"
																				onClick={() =>
																					handleRemoveItem(
																						item.Product.product_id
																					)
																				}>
																				<DeleteIcon />
																			</IconButton>
																		</TableCell>
																	</TableRow>
																))}
															</React.Fragment>
														);
													})}
												</TableBody>
											</Table>
										</TableContainer>
									</CardContent>
								</Card>
								<Button variant="outlined" color="primary" onClick={clearCart}>
									Clear Cart
								</Button>
							</Box>

							{/* Order Summary Section */}
							<Box sx={{ flex: 0.4 }}>
								<Card className="order-summary-card">
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Quote Summary
										</Typography>
										<Box className="summarySubtotal">
											<Box className="summary-row">
												<Typography>Subtotal</Typography>
												<Typography>
													{currencySymbol}
													{calculateSubtotal().toFixed(2)}
												</Typography>
											</Box>
											{/* <Box className="summary-row">
										<Typography>Tax (10%)</Typography>
										<Typography>
											{currencySymbol}
											{calculateTax().toFixed(2)}
										</Typography>
									</Box> */}
										</Box>
										<Box className="summary-row total">
											<Typography variant="h6">Total</Typography>
											<Typography variant="h6">
												{currencySymbol}
												{calculateTotal().toFixed(2)}
											</Typography>
										</Box>
										<Box className="summary-row total">
											<Typography variant="body1" sx={{ fontSize: "11px" }}>
												Note: Our default shipping is UPS ground, PP&A, UNLESS OTHERWISE
												SPECIFIED. Shipping fees & Taxes will be added to the invoice.
											</Typography>
										</Box>
										<Box className="summary-actions">
												{user.user_role === "Viewer" ? (
													<NotifyPurchaser data={cartProducts} />
												) : (


														<Button
															variant="contained"
															color="primary"
															fullWidth
															className="checkout-button"
															onClick={() => {
																navigate("/checkout");
															}}
															disabled={
																user.user_role === "Viewer" ||
																calculateTotal().toFixed(2) <
																(parseFloat(appOptions.minOrderAmount) || parseFloat(0))
															}>
															Checkout
														</Button>
												)}
											{calculateTotal().toFixed(2) <
												(parseFloat(appOptions.minOrderAmount) || parseFloat(0)) && (
												<Typography variant="body1" color="error">
													Minimum Order Amount should be {currencySymbol}
													{parseFloat(appOptions.minOrderAmount) || parseFloat(0)}, Your
													Current OrderTotal is {currencySymbol}
													{calculateTotal().toFixed(2)}
												</Typography>
											)}
											{user.user_role === "Viewer" && (
													<>
												<Typography variant="body1" color="error">
													You are not authorized to place an order. <br />
															Please contact the customer care for further details, <br />
															or you can instead notify purchasers or BPA Customer Support to place an order on your behalf.

												</Typography>
													</>
											)}
										</Box>
									</CardContent>
								</Card>
								<Typography variant="body1" sx={{ fontSize: "12px", mt: 2 }}>
									View Request List Parts:{" "}
									<Link
										underline="hover"
										sx={{ cursor: "pointer" }}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleTabChange(e, 1);
										}}>
										Click Here
									</Link>
								</Typography>
							</Box>
						</Box>
					)}


				</>
			)}

			{tab === 1 && (
				<>
					<RequestList />
				</>
			)}


		</Container>
	);
}

export default Cart;
